(() => {
  if (!document.body?.matches('.drpenx-home-final,.drpenx-collection-final,.drpenx-product-final,.drpenx-cart-final')) return;
  const key = Symbol.for('drpenx.home.chat.clearance');
  if (window[key]) { window[key].refresh(); return; }

  const selectors = '.category-all,.text-link,.light-button,.cart-button,.model-tabs button,.film-rail-controls button,.library-play,.newsletter-input button,.buy-action,.image-toggle,.family-tabs button,.family-tabs a,.collection-pagination a,.collection-promo a,.collection-editorial__cta,.collection-sort select,.primary-button,.gallery-enlarge,.gallery-pagination button,.gallery-thumbs,.quick-buy-native,.commerce-button,.quantity button,.quantity input,.remove-item,.discount-form button,.discount-form input,.reco-link,.reco-controls button,.mobile-checkout,.page-index a,.quantity-control button,.product-option select,.jdgm-paginate__page,.jdgm-write-rev-link,.jdgm-sort-dropdown,.spec-links a,.decision-links a,#offer-price';
  const gap = 8;
  // Observed native wrapper offset, not an official Shopify API. Missing support leaves the widget unchanged.
  const offsetProperty = '--shopify-chat-activator-offset';
  let baseOffset = '', originalOffset = '', originalPriority = '', wasOpen = false, resumeShift = 0;
  let host, activator, panel, frame = 0, shift = 0;
  let actions = [], visible = new Set();
  const observedRoots = new Set();
  const waitingNames = new Set();
  const rootObservers = [];
  const rect = (r, offset = 0) => ({ left: r.left, right: r.right, top: r.top + offset, bottom: r.bottom + offset, width: r.width, height: r.height });
  const overlaps = (a, b) => a.left < b.right + gap && a.right > b.left - gap && a.top < b.bottom + gap && a.bottom > b.top - gap;
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const setShift = value => {
    value = Math.round(value * 100) / 100;
    if (!host || Math.abs(value - shift) < .1) return;
    shift = value;
    if (!shift) {
      if (originalOffset) host.style.setProperty(offsetProperty, originalOffset, originalPriority);
      else host.style.removeProperty(offsetProperty);
    } else host.style.setProperty(offsetProperty, `calc(${baseOffset} + ${-shift}px)`);
  };

  function update() {
    frame = 0;
    if (!activator?.isConnected || !host?.isConnected || !baseOffset) return;
    if (activator.getAttribute('aria-expanded') === 'true' || (panel && panel.getBoundingClientRect().width > 0)) {
      if (!wasOpen) { resumeShift = shift; wasOpen = true; setShift(0); }
      return;
    }
    if (wasOpen) { wasOpen = false; setShift(resumeShift); resumeShift = 0; schedule(); return; }
    if (activator.matches(':hover,:focus-visible') || activator === activator.getRootNode().activeElement) return;
    const measured = activator.getBoundingClientRect();
    if (!measured.width || !measured.height) return;
    const natural = rect(measured, -shift);
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (window.visualViewport?.height || innerHeight);
    const targets = [];
    const measuredActions = new Set([...visible, ...document.querySelectorAll('#CartMainContent [data-cf-sticky]')]);
    for (const action of measuredActions) {
      if (!action.isConnected || action.disabled || action.closest('button')?.disabled) continue;
      const bounds = action.getBoundingClientRect();
      if (bounds.width && bounds.height && bounds.bottom > viewportTop && bounds.top < viewportBottom && bounds.left < natural.right + gap && bounds.right > natural.left - gap) targets.push(rect(bounds));
    }
    if (!targets.some(target => overlaps(natural, target))) { setShift(0); return; }
    const candidates = [...new Set(targets.map(target => Math.min(0, target.top - gap - natural.bottom)))].sort((a, b) => b - a);
    const safe = candidates.find(candidate => natural.top + candidate >= viewportTop + gap && !targets.some(target => overlaps(rect(natural, candidate), target)));
    setShift(safe === undefined ? 0 : safe);
  }

  const intersections = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target);
    schedule();
  }) : null;
  const sizes = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;

  function refreshActions() {
    intersections?.disconnect();
    const mainSelector = document.body.matches('.drpenx-product-final') ? '#ProductMainContent' : document.body.matches('.drpenx-cart-final') ? '#CartMainContent' : '#MainContent';
    actions = [...new Set([
      ...document.querySelectorAll(mainSelector + ' ' + selectors.split(',').join(',' + mainSelector + ' ')),
      ...document.querySelectorAll('.dxs-footer summary,.dxs-footer a')
    ])];
    visible = new Set(actions);
    for (const action of actions) intersections?.observe(action);
    schedule();
  }

  function publicPart(root, name, depth = 0) {
    if (!root || depth > 3) return null;
    const direct = root.querySelector(`[part~="${name}"]`);
    if (direct) return direct;
    for (const exported of root.querySelectorAll('[exportparts]')) {
      const mapping = exported.getAttribute('exportparts').split(',').map(token => token.trim().split(':').map(value => value.trim()));
      const match = mapping.find(([inner, outer]) => (outer || inner) === name);
      if (!match) continue;
      const found = publicPart(exported.shadowRoot, match[0], depth + 1);
      if (found) return found;
    }
    return null;
  }

  function observeExports(root, depth = 0) {
    if (!root || depth > 3) return;
    if (!observedRoots.has(root)) {
      observedRoots.add(root);
      const observer = new MutationObserver(bindParts);
      observer.observe(root, { childList: true, subtree: true });
      rootObservers.push(observer);
    }
    for (const exported of root.querySelectorAll('[exportparts]')) {
      if (!/\b(?:activator|dialog)\b/.test(exported.getAttribute('exportparts'))) continue;
      if (exported.localName.includes('-') && !waitingNames.has(exported.localName)) {
        waitingNames.add(exported.localName);
        customElements.whenDefined(exported.localName).then(() => requestAnimationFrame(bindParts));
      }
      observeExports(exported.shadowRoot, depth + 1);
    }
  }

  function bindParts() {
    if (!host?.isConnected) return;
    observeExports(host.shadowRoot);
    const found = publicPart(host.shadowRoot, 'activator');
    if (!found || found === activator) { schedule(); return; }
    const root = found.getRootNode();
    let consumesOffset = false;
    const inspectRules = rules => {
      for (const rule of rules) {
        if (rule.style?.getPropertyValue('bottom').includes(`var(${offsetProperty}`)) consumesOffset = true;
        if (rule.cssRules) inspectRules(rule.cssRules);
      }
    };
    try {
      for (const sheet of [...root.adoptedStyleSheets, ...[...root.querySelectorAll('style')].map(style => style.sheet).filter(Boolean)]) inspectRules(sheet.cssRules);
    } catch { return; }
    const nativeOffset = baseOffset || getComputedStyle(host).getPropertyValue(offsetProperty).trim();
    if (!consumesOffset || !nativeOffset || !CSS.supports('bottom', `calc(${nativeOffset} + 1px)`)) return;
    baseOffset = nativeOffset;
    if (activator) sizes?.unobserve(activator);
    activator = found;
    panel = publicPart(host.shadowRoot, 'dialog');
    panel?.addEventListener('animationend', schedule);
    panel?.addEventListener('transitionend', schedule);
    sizes?.observe(activator);
    const state = new MutationObserver(schedule);
    state.observe(activator, { attributes: true, attributeFilter: ['aria-expanded', 'hidden'] });
    rootObservers.push(state);
    activator.addEventListener('animationend', schedule);
    activator.addEventListener('transitionend', schedule);
    activator.addEventListener('blur', schedule);
    activator.addEventListener('mouseleave', schedule);
    schedule();
  }

  function attach() {
    const next = document.querySelector('shopify-chat');
    if (!next) return false;
    if (next !== host) {
      for (const observer of rootObservers) observer.disconnect();
      rootObservers.length = 0;
      observedRoots.clear();
      waitingNames.clear();
      if (activator) sizes?.unobserve(activator);
      host = next;
      activator = panel = null;
      shift = 0;
      baseOffset = '';
      originalOffset = host.style.getPropertyValue(offsetProperty);
      originalPriority = host.style.getPropertyPriority(offsetProperty);
      wasOpen = false;
      resumeShift = 0;
      customElements.whenDefined(host.localName).then(() => requestAnimationFrame(bindParts));
      bindParts();
    }
    return true;
  }

  function refresh() { refreshActions(); attach(); }
  function refreshCartLayout() {
    refresh();
    if (frame) cancelAnimationFrame(frame);
    update();
  }
  if (!attach()) {
    const arrival = new MutationObserver(() => { if (attach()) arrival.disconnect(); });
    arrival.observe(document.documentElement, { childList: true, subtree: true });
  }
  refreshActions();
  sizes?.observe(document.querySelector('#MainContent') || document.body);
  document.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pageshow', refresh);
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);
  document.addEventListener('cart:updated', refresh);
  document.addEventListener('drpenx:cart-sticky-layout', refreshCartLayout);
  document.fonts?.ready.then(schedule);
  window[key] = { refresh };
})();
